import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerInternal, uiMarkerLayout } from './shared';

/**
 * APP SHELL
 *
 * The frame every signed-in screen renders inside: a sidebar down the left,
 * a top bar, and the page under it. The shell is the height of the viewport
 * and never scrolls itself — only `main` does — so the sidebar and the top
 * bar hold still while a long table scrolls under them.
 *
 * The header and the main are landmarks: a screen reader user jumps to
 * `banner` and `main` by name, so they are real elements rather than divs
 * with roles.
 */
export const appShellRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex h-screen w-full items-stretch overflow-hidden',
]);

/** The column beside the sidebar: the top bar, the page, and what floats over them. */
export const appShellColumnRecipe = cva([
  uiMarkerInternal,
  'flex min-w-0 grow flex-col overflow-hidden',
]);

/** Header over main, sharing the column's height between them. */
export const appShellBodyRecipe = cva([
  uiMarkerInternal,
  'flex min-h-0 grow flex-col overflow-x-hidden',
]);

export const appShellHeaderRecipe = cva([
  uiMarker,
  'flex h-16 shrink-0 items-center justify-between border-b border-border px-4 md:px-8',
]);

/*
 * `main` scrolls and carries no inset of its own: a pinned element inside a
 * padded scroller stops short of the top by the padding, and content shows in
 * the strip above it. The inset sits on the page box inside it instead.
 */
export const appShellMainRecipe = cva([
  uiMarker,
  'w-full max-w-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden',
]);

/**
 * The box the page fills. Full height, so a page that sizes itself to the
 * shell (the inspections tab, whose columns scroll on their own) still has a
 * height to fill.
 */
export const appShellPageRecipe = cva([uiMarker, 'h-full'], {
  variants: {
    /** The page's inset. `none` for a screen that draws edge to edge — the studio. */
    padding: {
      none: '',
      md: 'p-4 md:p-8',
    },
  },
  defaultVariants: { padding: 'md' },
});

export type AppShellPageRecipeProps = VariantProps<typeof appShellPageRecipe>;
